import {useTranslation} from '@aboutyou/i18n';
import React from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {FONT_WEIGHTS} from '../../../constants/typography';
import type {ImageType} from '../../../types/Image';
import {Button} from '../../atoms/Buttons/Button';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName} from '../../atoms/Icon/IconName';
import {Image} from '../../atoms/Image/Image';
import {Text} from '../../atoms/Text/Text';

export interface TeaserImageProps {
  image: ImageType;
  content?:
    | {
        likeButton: React.ReactNode;
        type: 'likeButton';
      }
    | {
        type: 'cta';
        headline: string;
        subline: string;
        cta: string;
      };
  href?: string;
  className?: string;
}

const breakpoints = [
  {
    breakpoint: '(max-width: 767px)',
    columns: 2,
  },
  {
    breakpoint: '(max-width: 1023px)',
    columns: 3,
  },
  {
    columns: 4,
  },
];
const possibleImageWidths = [600, 800, 960, 1200];

export const TeaserImage: React.FC<TeaserImageProps> = ({
  image,
  content,
  href,
  className,
}) => {
  const {t} = useTranslation();

  return (
    <TeaserImageWrapper
      data-testid={`teaserImage`}
      $scaleOnHover={content?.type === 'likeButton'}
      className={className}
    >
      <StyledImage
        alt={image.description}
        breakpoints={breakpoints}
        ratio="3:4"
        src={image.url}
        data-testid={'profileTileImage'}
        loading="lazy"
        widths={possibleImageWidths}
      />

      <ImageShade data-testid="imageShade">
        <CallToActionButton variant="glassy" href={href} size="fit-width">
          {t('Shop the outfit')}
        </CallToActionButton>
      </ImageShade>
      {content?.type === 'likeButton' && content.likeButton}

      {content?.type === 'cta' && (
        <TeaserTextWrapper>
          <Subline size="px16">{content.subline}</Subline>
          <Headline size="px40">{content.headline}</Headline>
          <CallToActionInline size="px14">{content.cta}</CallToActionInline>
        </TeaserTextWrapper>
      )}
    </TeaserImageWrapper>
  );
};

export const TeaserLikeButton: React.FC<{
  isLiked: boolean;
  onTeaserLike: () => void;
}> = ({isLiked, onTeaserLike}) => {
  const {t} = useTranslation();
  const handleTeaserLike = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    onTeaserLike();
  };
  return (
    <LikeButton
      onClick={handleTeaserLike}
      variant="glassy"
      size="sm"
      round
      aria-label={isLiked ? t('Item in whishlist') : t('Add item to whishlist')}
    >
      <Icon
        size="S"
        asset={isLiked ? GenericIconName.HeartFilled : GenericIconName.Heart}
        fill="white"
        data-testid="likeButton"
      />
    </LikeButton>
  );
};

const hoverTransitionTiming = '500ms ease';

const LikeButton = styled(Button)`
  position: absolute;
  top: 22px;
  right: 22px;
`;

const TeaserTextWrapper = styled.div`
  position: absolute;
  color: initial;
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  background: linear-gradient(rgba(0, 0, 0, 0.15), rgba(0, 0, 0, 0.2));
`;

const Subline = styled(Text)`
  color: ${COLORS.White};
  margin-bottom: 2rem;
  text-transform: uppercase;
`;

const Headline = styled(Text)`
  color: ${COLORS.White};
  text-align: center;
  width: 80%;
  margin-bottom: 2rem;
  text-transform: uppercase;
`;

const CallToActionInline = styled(Text)`
  color: ${COLORS.White};
  border-bottom: solid 1px ${COLORS.White};
`;

const StyledImage = styled(Image)`
  object-fit: cover;
  transform-origin: center center;
  transition: transform ${hoverTransitionTiming};
  width: 100%;
  height: 100%;
`;

const ImageShade = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  max-height: 100%;
  max-width: 100%;
  border-radius: 2px;
  background-color: rgba(0, 0, 0, 0);
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color ${hoverTransitionTiming};
`;

const CallToActionButton = styled(Button)`
  font-weight: ${FONT_WEIGHTS.mediumBold};
  opacity: 0;
  transition: var(--default-transition), opacity ${hoverTransitionTiming};
`;

const TeaserImageWrapper = styled.div<{$scaleOnHover: boolean}>`
  position: relative;
  color: initial;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  position: relative;
  aspect-ratio: 3 / 4;
  border-radius: 4px;
  @media (pointer: fine) {
    &:hover {
      ${StyledImage} {
        transform: ${({$scaleOnHover}) =>
          $scaleOnHover ? 'scale(1.05)' : 'none'};
      }
      ${ImageShade} {
        background-color: rgba(0, 0, 0, 0.4);
      }
      ${CallToActionButton} {
        opacity: 1;
      }
    }
  }
`;
